import { tenantApi } from '@/modules/tenant/services/tenantApi';
import type { Quote, QuoteFormData, QuoteInventoryDrift } from '../types/crm.types';

export const quoteApi = {
  // Get all quotes (paginated)
  getQuotes: (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    quote_status?: string;
    created_by?: string | string[];
    expiring_soon?: boolean;
    sort_by?: string;
    sort_order?: string;
    deleted?: 'only' | 'with';
    /** #480/#481 — committed-quote guard + sibling filters */
    inventory_item_id?: string;
    committed?: boolean;
    active?: boolean;
  }) => tenantApi.getPaginated<Quote>('/quotes', { params }),

  // Get single quote
  getQuote: (id: string, params?: { include_deleted?: boolean }) =>
    tenantApi.getOne<Quote>(`/quotes/${id}`, { params }),

  /** Create new quote */
  createQuote: (quoteData: QuoteFormData) =>
    tenantApi.postOne<Quote>('/quotes', quoteData),

  /** Update existing quote */
  updateQuote: (id: string, quoteData: Partial<QuoteFormData>) =>
    tenantApi.putOne<Quote>(`/quotes/${id}`, quoteData),

  /** Delete quote */
  deleteQuote: (id: string) =>
    tenantApi.deleteOne(`/quotes/${id}`),

  /** Send quote to customer */
  sendQuote: (payload: { id: string; recipient?: 'buyer' | 'co_buyer' }) =>
    tenantApi.postAction(`/quotes/${payload.id}/send`, {
      recipient: payload.recipient,
    }),

  /** Accept quote */
  acceptQuote: (id: string) =>
    tenantApi.postAction(`/quotes/${id}/accept`),

  /** Decline quote */
  declineQuote: (id: string, reason?: string) =>
    tenantApi.postAction(`/quotes/${id}/decline`, { decline_reason: reason }),

  /** Preview quote HTML */
  previewQuote: (quoteId: string) =>
    tenantApi.postOne<{ success: boolean; html: string }>(`/quotes/${quoteId}/preview`),

  /** Approve quote and generate PDF */
  approveQuote: (
    quoteId: string,
    recipient?: 'buyer' | 'co_buyer',
    documentTitle?: string
  ) => tenantApi.postOne<{ success: boolean; download_url?: string; view_url?: string }>(`/quotes/${quoteId}/approve`, {
    ...(recipient ? { recipient } : {}),
    ...(documentTitle ? { document_title: documentTitle } : {}),
  }),

  /** Get generated PDFs for a quote */
  getQuotePdfs: (quoteId: string) =>
    tenantApi.getList(`/quotes/${quoteId}/pdfs`),

  /** Reject quote generation */
  rejectQuote: (quoteId: string, reason?: string) =>
    tenantApi.postAction(`/quotes/${quoteId}/reject`, { reason }),

  /** Snapshot vs live inventory drift — powers the staleness banner (#436) */
  getInventoryDrift: (quoteId: string) =>
    tenantApi.getOne<QuoteInventoryDrift>(`/quotes/${quoteId}/inventory-drift`),

  /** Supersede quote with a fresh revision built from live inventory (#436) */
  supersedeQuote: (quoteId: string) =>
    tenantApi.postOne<Quote>(`/quotes/${quoteId}/supersede`),
};
